import { useTranslation } from 'react-i18next';
import type { WorkspaceIntakeSubmissionListItem } from '@/api/intake/intakeApi.types';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { IntakeStatusBadge } from '@/components/common/IntakeStatusBadge';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';

interface IntakeInboxTableProps {
  submissions: WorkspaceIntakeSubmissionListItem[];
  hasFilters: boolean;
  onOpenSubmission: (submissionId: string) => void;
}

export function IntakeInboxTable({
  submissions,
  hasFilters,
  onOpenSubmission,
}: IntakeInboxTableProps) {
  const { t } = useTranslation('intake');

  if (submissions.length === 0) {
    return (
      <Card as="section" className="py-12 flex flex-col items-center text-center">
        <span className="w-12 h-12 rounded-full bg-zoovpalelavender flex items-center justify-center text-zooviolet">
          <Icon name="ClipboardList" />
        </span>
        <p className="mt-3 text-sm text-zoomuted">
          {hasFilters ? t('inbox.noMatches') : t('inbox.empty')}
        </p>
      </Card>
    );
  }

  return (
    <Card as="section" padding="none">
      <ul className="divide-y divide-zooborder">
        {submissions.map(function renderSubmission(submission) {
          return (
            <li key={submission.id}>
              <button
                type="button"
                onClick={function open() {
                  onOpenSubmission(submission.id);
                }}
                className="w-full text-left px-4 py-3.5 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:items-center hover:bg-zoovpalelavender transition-colors"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-zooindigo truncate">
                    {submission.petName === null ? t('inbox.unnamedPet') : submission.petName}
                  </span>
                  {submission.isNewPet ? (
                    <Badge tone="accent" size="sm" className="mt-1">
                      {t('inbox.newPet')}
                    </Badge>
                  ) : null}
                </span>
                <span className="text-sm text-zoomuted truncate">{submission.clientName}</span>
                <span className="text-xs text-zoomuted">
                  {submission.submittedAt === null
                    ? '—'
                    : formatIsoDateTimeToTimestamp(submission.submittedAt)}
                </span>
                <span className="sm:justify-self-end">
                  <IntakeStatusBadge status={submission.status} />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
